// SingleTeam.jsx
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Grid,
    IconButton,
    List,
    ListItem,
    ListItemText,
    Paper,
    Tab,
    Tabs,
    TextField,
    Typography,
} from "@mui/material";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import {
    addDoc,
    arrayRemove,
    arrayUnion,
    collection,
    deleteDoc,
    doc,
    getDoc,
    getDocs,
    updateDoc,
} from "firebase/firestore";

import { getDownloadURL, listAll, ref } from "firebase/storage";

import { db, storage } from "../firebase-key";

// icons
import { FaEdit, FaPlus, FaTrash, FaUserPlus } from "react-icons/fa";

const formatValue = (val) => {
  if (!val) return "-";

  if (val?.seconds) {
    return new Date(val.seconds * 1000).toLocaleString(); // Firestore timestamp
  }

  return val.toString();
};

/** format key for UI **/
const formatKey = (k) =>
  k
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .replace("Uid", "ID");

const SingleTeam = () => {
  const { uid } = useParams(); // team id
  const navigate = useNavigate();

  const [team, setTeam] = useState(null);
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("info");

  // customers
  const [allCustomers, setAllCustomers] = useState([]);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [assignLoading, setAssignLoading] = useState(false);
  const [customerSearch, setCustomerSearch] = useState("");

  // tasks
  const [tasks, setTasks] = useState([]);
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [taskForm, setTaskForm] = useState({
    title: "",
    description: "",
    id: null,
  });
  const [tasksLoading, setTasksLoading] = useState(false);

  // load team
  const loadTeam = useCallback(async () => {
    setLoading(true);
    try {
      const snap = await getDoc(doc(db, "teams", uid));
      if (!snap.exists()) {
        toast.error("Team not found");
        navigate("/owner");
        return;
      }
      setTeam({ uid, ...snap.data() });
    } catch (err) {
      console.error("loadTeam error", err);
      toast.error("Failed to load team");
      navigate("/owner");
    } finally {
      setLoading(false);
    }
  }, [navigate, uid]);

  // load documents
  const loadDocuments = useCallback(async () => {
    setDocs([]);
    try {
      const folderRef = ref(storage, `team_documents/${uid}`);
      const res = await listAll(folderRef);
      const out = [];

      for (const item of res.items) {
        try {
          const url = await getDownloadURL(item);
          out.push({ name: item.name, url });
        } catch (err) {}
      }

      setDocs(out);
    } catch (err) {
      setDocs([]);
    }
  }, [uid]);

  // load all customers from users collection
  const loadAllCustomers = useCallback(async () => {
    try {
      const snap = await getDocs(collection(db, "users"));
      const users = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setAllCustomers(users);
    } catch (err) {
      console.error("loadAllCustomers", err);
      toast.error("Failed loading customers");
    }
  }, []);

  // load tasks
  const loadTasks = useCallback(async () => {
    setTasksLoading(true);
    try {
      const qSnap = await getDocs(collection(db, "teams", uid, "tasks"));
      const rows = qSnap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .sort((a, b) => (a.created_at > b.created_at ? -1 : 1));
      setTasks(rows);
    } catch (err) {
      console.error("loadTasks", err);
      toast.error("Failed to load tasks");
    } finally {
      setTasksLoading(false);
    }
  }, [uid]);

  // initial load
  useEffect(() => {
    if (!uid) return;
    loadTeam();
    loadDocuments();
    loadAllCustomers();
    loadTasks();
  }, [uid, loadTeam, loadDocuments, loadAllCustomers, loadTasks]);

  // assigned customers
  const assignedCustomers =
    team && Array.isArray(team.customers)
      ? allCustomers.filter((c) => team.customers.includes(c.uid))
      : [];

  // customers available for this team (not yet assigned)
  const availableCustomers = allCustomers.filter((c) => {
    const arr = Array.isArray(c.employeeId) ? c.employeeId : [];
    return !arr.includes(team?.uid);
  });

  /** ASSIGN CUSTOMER -> team */
  const assignCustomer = async (customerId) => {
    if (!team) return;
    setAssignLoading(true);

    try {
      const userSnap = await getDoc(doc(db, "users", customerId));
      const userData = userSnap.exists() ? userSnap.data() : null;

      const employeeArr = Array.isArray(userData?.employeeId)
        ? userData.employeeId
        : [];

      // already assigned?
      if (employeeArr.includes(team.uid)) {
        toast.error("Customer already assigned to this team");
        setAssignLoading(false);
        return;
      }

      // update team: add customer
      await updateDoc(doc(db, "teams", team.uid), {
        customers: arrayUnion(customerId),
      });

      // update user: add team to employeeId list
      await updateDoc(doc(db, "users", customerId), {
        employeeId: arrayUnion(team.uid),
      });

      toast.success("Customer assigned!");
      await loadTeam();
      await loadAllCustomers();
    } catch (err) {
      console.error("assignCustomer", err);
      toast.error("Failed to assign customer");
    } finally {
      setAssignLoading(false);
    }
  };

  /** UNASSIGN CUSTOMER */
  const unassignCustomer = async (customerId) => {
    if (!team) return;
    if (!window.confirm("Remove this customer from team?")) return;

    try {
      await updateDoc(doc(db, "teams", team.uid), {
        customers: arrayRemove(customerId),
      });

      await updateDoc(doc(db, "users", customerId), {
        employeeId: arrayRemove(team.uid),
      });

      toast.success("Customer unassigned");
      await loadTeam();
      await loadAllCustomers();
    } catch (err) {
      console.error("unassignCustomer", err);
      toast.error("Failed to unassign");
    }
  };

  /** TASK OPERATIONS */
  const openCreateTask = () => {
    setTaskForm({ title: "", description: "", id: null });
    setTaskDialogOpen(true);
  };

  const openEditTask = (t) => {
    setTaskForm({ title: t.title, description: t.description, id: t.id });
    setTaskDialogOpen(true);
  };

  const saveTask = async () => {
    const { title, description, id } = taskForm;
    if (!title.trim()) {
      toast.error("Title required");
      return;
    }

    try {
      if (id) {
        await updateDoc(doc(db, "teams", uid, "tasks", id), {
          title,
          description,
          updated_at: new Date().toISOString(),
        });
        toast.success("Task updated");
      } else {
        await addDoc(collection(db, "teams", uid, "tasks"), {
          title,
          description,
          status: "pending",
          created_at: new Date().toISOString(),
        });
        toast.success("Task created");
      }

      setTaskDialogOpen(false);
      loadTasks();
    } catch (err) {
      console.error("saveTask", err);
      toast.error("Failed saving task");
    }
  };

  const toggleTaskStatus = async (task) => {
    try {
      await updateDoc(doc(db, "teams", uid, "tasks", task.id), {
        status: task.status === "done" ? "pending" : "done",
        updated_at: new Date().toISOString(),
      });

      loadTasks();
    } catch (err) {
      console.error(err);
      toast.error("Failed updating");
    }
  };

  const deleteTask = async (task) => {
    if (!window.confirm("Delete task?")) return;

    try {
      await deleteDoc(doc(db, "teams", uid, "tasks", task.id));
      toast.success("Task deleted");
      loadTasks();
    } catch (err) {
      console.error(err);
      toast.error("Failed deleting");
    }
  };

  /** UI LOADING */
  if (loading) {
    return (
      <Box
        sx={{
          minHeight: "60vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!team) {
    return (
      <Box
        sx={{
          minHeight: "60vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Typography>Team not found</Typography>
      </Box>
    );
  }

  /** RENDER UI */
  return (
    <Box sx={{ px: 3, py: 12 }}>
      <ToastContainer />

      <Button variant="text" onClick={() => navigate("/owner")} sx={{ mb: 2 }}>
        ← Back to dashboard
      </Button>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Box>
          <Typography variant="h4" fontWeight={700}>
            {team.name || team.email || `Team ${team.uid}`}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {team.email}
          </Typography>
        </Box>

        <Box>
          <Button
            startIcon={<FaUserPlus />}
            variant="contained"
            onClick={() => setAssignDialogOpen(true)}
            sx={{ mr: 1 }}
          >
            Assign Customer
          </Button>
          <Button variant="outlined" onClick={() => setTab("tasks")}>
            Tasks
          </Button>
        </Box>
      </Box>

      <Divider sx={{ mb: 3 }} />

      <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ mb: 3 }}>
        <Tab value="info" label="Information" />
        <Tab value="docs" label="Documents" />
        <Tab value="customers" label="Assigned Customers" />
        <Tab value="tasks" label="Tasks" />
      </Tabs>

      {/* INFO TAB */}
      {tab === "info" && (
        <Paper sx={{ p: 3, mb: 4 }}>
          <Grid container spacing={2}>
            {Object.keys(team)
              .filter((k) => !["password", "uid", "customers"].includes(k))
              .map((key) => (
                <Grid item xs={12} md={6} key={key}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {formatKey(key)}
                  </Typography>
                  <Typography variant="body1">{formatValue(team[key])}</Typography>
                </Grid>
              ))}

            {team.created_at && (
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Created At
                </Typography>
                <Typography variant="body1">
                  {new Date(team.created_at).toLocaleString()}
                </Typography>
              </Grid>
            )}
          </Grid>
        </Paper>
      )}

      {/* DOCUMENTS TAB */}
      {tab === "docs" && (
        <Paper sx={{ p: 3, mb: 4 }}>
          <Typography variant="h6" mb={2}>
            Documents
          </Typography>

          {docs.length === 0 ? (
            <Typography color="text.secondary">No documents uploaded.</Typography>
          ) : (
            <Grid container spacing={2}>
              {docs.map((d, i) => (
                <Grid item xs={12} md={6} key={i}>
                  <Paper sx={{ p: 2 }}>
                    <Typography fontWeight={600}>{d.name}</Typography>
                    <Box sx={{ mt: 1 }}>
                      <Button
                        component="a"
                        href={d.url}
                        target="_blank"
                        rel="noreferrer"
                        size="small"
                      >
                        View / Download
                      </Button>
                    </Box>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          )}
        </Paper>
      )}

      {/* ASSIGNED CUSTOMERS */}
      {tab === "customers" && (
        <Paper sx={{ p: 3, mb: 4 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
            <Typography variant="h6">Assigned Customers</Typography>
            <Button
              onClick={() => setAssignDialogOpen(true)}
              startIcon={<FaPlus />}
              variant="contained"
            >
              Assign New
            </Button>
          </Box>

          {assignedCustomers.length === 0 ? (
            <Typography color="text.secondary">
              No customers assigned to this team.
            </Typography>
          ) : (
            <List>
              {assignedCustomers.map((c) => (
                <ListItem
                  key={c.id}
                  secondaryAction={
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <Button
                        size="small"
                        onClick={() => navigate(`/team-customer/${c.uid}`)}
                      >
                        View
                      </Button>
                      <Button
                        color="error"
                        size="small"
                        onClick={() => unassignCustomer(c.uid)}
                      >
                        Unassign
                      </Button>
                    </Box>
                  }
                >
                  <ListItemText
                    primary={c.customer_name || c.name || c.email}
                    secondary={c.email}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </Paper>
      )}

      {/* TASKS */}
      {tab === "tasks" && (
        <Paper sx={{ p: 3 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              mb: 2,
              alignItems: "center",
            }}
          >
            <Typography variant="h6">Tasks</Typography>
            <Button
              variant="contained"
              startIcon={<FaPlus />}
              onClick={openCreateTask}
            >
              New Task
            </Button>
          </Box>

          {tasksLoading ? (
            <CircularProgress />
          ) : tasks.length === 0 ? (
            <Typography color="text.secondary">No tasks yet.</Typography>
          ) : (
            <List>
              {tasks.map((t) => (
                <ListItem
                  key={t.id}
                  secondaryAction={
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <Button size="small" onClick={() => toggleTaskStatus(t)}>
                        {t.status === "done" ? "Mark Pending" : "Mark Done"}
                      </Button>
                      <IconButton size="small" onClick={() => openEditTask(t)}>
                        <FaEdit />
                      </IconButton>
                      <IconButton size="small" onClick={() => deleteTask(t)}>
                        <FaTrash />
                      </IconButton>
                    </Box>
                  }
                >
                  <ListItemText
                    primary={
                      <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                        <Typography
                          sx={{
                            textDecoration:
                              t.status === "done" ? "line-through" : "none",
                          }}
                        >
                          {t.title}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          • {t.status}
                        </Typography>
                      </Box>
                    }
                    secondary={t.description}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </Paper>
      )}

      {/* ASSIGN CUSTOMER DIALOG */}
      <Dialog
        open={assignDialogOpen}
        onClose={() => setAssignDialogOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          Assign Customer to {team.name || team.email}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <TextField
              fullWidth
              placeholder="Search by name or email"
              value={customerSearch}
              onChange={(e) => setCustomerSearch(e.target.value)}
            />
          </Box>

          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Available Customers
          </Typography>

          <List sx={{ maxHeight: 320, overflow: "auto" }}>
            {availableCustomers.length === 0 && (
              <Typography color="text.secondary">No customers available</Typography>
            )}

            {availableCustomers
              .filter((c) => {
                if (!customerSearch) return true;
                const q = customerSearch.toLowerCase();
                return (
                  (c.customer_name || c.name || "")
                    .toLowerCase()
                    .includes(q) ||
                  (c.email || "").toLowerCase().includes(q)
                );
              })
              .map((c) => (
                <ListItem
                  key={c.id}
                  secondaryAction={
                    <Button
                      disabled={assignLoading}
                      onClick={() => assignCustomer(c.id)}
                    >
                      Assign
                    </Button>
                  }
                >
                  <ListItemText
                    primary={c.customer_name || c.name || c.email}
                    secondary={c.email}
                  />
                </ListItem>
              ))}
          </List>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setAssignDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* TASK CREATE / EDIT DIALOG */}
      <Dialog
        open={taskDialogOpen}
        onClose={() => setTaskDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {taskForm.id ? "Edit Task" : "Create Task"}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: "grid", gap: 2, mt: 1 }}>
            <TextField
              label="Title"
              value={taskForm.title}
              onChange={(e) =>
                setTaskForm((s) => ({ ...s, title: e.target.value }))
              }
            />
            <TextField
              label="Description"
              multiline
              rows={4}
              value={taskForm.description}
              onChange={(e) =>
                setTaskForm((s) => ({ ...s, description: e.target.value }))
              }
            />
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setTaskDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={saveTask}>
            {taskForm.id ? "Save" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SingleTeam;
